import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import {
  DestinationSystem,
  EntityType,
  JobStatus,
  MigrationRunStatus,
  TargetEnvironment,
  type CompanyShellRow,
  type CompanyTemplateRef,
  type EntityRecordPatch,
  type MyPayEnvironment,
  type PatchCompanyShellRow,
  type ValidationReport,
} from "../../generated/core-api";
import { useOnboardingWorkload } from "./useOnboardingWorkload";
import { useOnboardingImport } from "./useOnboardingImport";
import { useOnboardingValidation } from "./useOnboardingValidation";
import { useOnboardingMigrationRun } from "./useOnboardingMigrationRun";
import { mapValidationReportToCompanyShellValidateResult } from "./companyShellWorkloadsResultMapper";
import { rowsToJsonImportFile } from "../utils/rowsToJsonImportFile";
import { resolveIfMatchSourceVersion } from "../utils/resolveIfMatchSourceVersion";
import type { CompanyShellValidateResult } from "../types";

function reportHasFindings(report: ValidationReport | null | undefined): boolean {
  return (
    (report?.errors?.length ?? 0) > 0 || (report?.warnings?.length ?? 0) > 0
  );
}

/**
 * LADR-039 (#440) Step 15 — the Slice 5 **workloads** REST replacement for
 * `useValidateCompanyShellProvision` / `useCommitCompanyShellProvision`
 * (Track 1, `invokeAgent`/Temporal `company_shell_provision`). Step 16 wires
 * this hook into `BulkCreateCompanyWizardProvider` /
 * `BulkCreateCompanyReviewScreen` behind `FeatureFlag.EnableWorkloadsCutover`
 * — this hook itself does not read the flag.
 *
 * Pipeline (plan Step 15 / `.plans/ladr-039-contract-mapping.md`'s company
 * shell vertical table):
 *
 *   import (multipart file, W1) --> optional PATCH (edited rows, W2) --> validations --> run
 *
 * **Divergence from `useValidateCompanyShellProvision`/`useCommitCompanyShellProvision`:**
 * Track 1 exposes two independent, stateless invocation hooks — each `invoke`
 * call resends `{ template, rows, mypayEnvironment }` in full, with the
 * wizard context (`BulkCreateCompanyWizardContext`) as the sole owner of
 * `rows`/`template`/`mypayEnvironment` state. Track 2 cannot work that way:
 * once a batch is imported, the server holds the canonical `company_shell`
 * rows, and re-validating after an edit means `PATCH`ing that held record —
 * a validate and a commit that would otherwise race for the same
 * workload/import/PATCH state need to share it. This hook therefore follows
 * the **one manager, owns its pending state** shape already established by
 * `useWorkPatternImportWorkloadsManager`/`usePayComponentImportWorkloadsManager`
 * (Steps 8/12) rather than Track 1's two-hook split: callers push
 * `rows`/`template`/`mypayEnvironment`/`file` in via `setPendingData`/
 * `updateRow`, mirroring `commitWorkflowId`'s resumability role via the
 * exposed `workloadId`/`runId`/`runStatus` instead.
 *
 * **No single `companyCode` to scope the workload by:** a bulk company-shell
 * batch *creates* many new companies (each with its own `company_code`
 * inside a row), unlike work patterns/pay components, which validate one
 * `companyCode` up front via `CompanyContextStep`. `useOnboardingWorkload`
 * is called with `companyCode: undefined, enabled: true` (the override added
 * in this same step) so the workload reuse key degrades to
 * `(workspace_id, destinationSystem)` alone for this vertical, instead of
 * staying permanently disabled waiting for a `companyCode` that never
 * arrives.
 *
 * **ETag / If-Match:** OpenAPI still omits a modelled `If-Match` parameter on
 * `patchOnboardingEntityRecord`, but `customCoreInstance` now merges the
 * `ETag` response header onto `EntityRecordView` as `etag` for downstream
 * optimistic concurrency when the generated client gains header support.
 */

const DESTINATION_SYSTEM = DestinationSystem.christmas;
const COMPANY_SHELL_ENTITY_TYPE = EntityType.company_shell;

/**
 * `PatchCompanyShellRow` (PATCH-only strict variant, `extra="forbid"`) is
 * field-name-identical to `CompanyShellRow` (shared with Track 1 CSV
 * parsing/editing, permissive `extra`) today — confirmed against the
 * generated docstrings in `src/generated/core-api.ts` (their per-field enum
 * sub-types differ only in name, e.g. `CompanyShellRowCountryCode` vs.
 * `PatchCompanyShellRowCountryCode`, with identical literal value sets).
 * Mapped field-by-field (rather than a cast/spread) so the one place the two
 * shapes could diverge in a future API change is this function, not every
 * call site — mirrors `toPatchWorkPatternRow` in
 * `useWorkPatternImportWorkloadsManager.ts`.
 */
function toPatchCompanyShellRow(row: CompanyShellRow): PatchCompanyShellRow {
  return {
    company_code: row.company_code,
    company_name: row.company_name,
    country_code: row.country_code,
    companyGroupName: row.companyGroupName,
    bureau_id: row.bureau_id,
    region_name: row.region_name,
    start_date: row.start_date,
    end_date: row.end_date,
    annual_leave_anniversary_date: row.annual_leave_anniversary_date,
    trading_name: row.trading_name,
    company_ird_no: row.company_ird_no,
    sick_leave_stand_down: row.sick_leave_stand_down,
    company_abn_number: row.company_abn_number,
    company_abn_branch_number: row.company_abn_branch_number,
    postal_state: row.postal_state,
    postal_code: row.postal_code,
    courier_state: row.courier_state,
    courier_code: row.courier_code,
    main_contact: row.main_contact,
    secondary_contact: row.secondary_contact,
    postal_address: row.postal_address,
    courier_address: row.courier_address,
    delivery: row.delivery,
    delivery_note: row.delivery_note,
    pay_schedules: row.pay_schedules,
    efiling_option: row.efiling_option,
    large_company: row.large_company,
    acr_product_type: row.acr_product_type,
    kiwisaver_option: row.kiwisaver_option,
    acc_classification_unit: row.acc_classification_unit,
    fte_standard_hours_per_week: row.fte_standard_hours_per_week,
    week_start_day: row.week_start_day,
  };
}

export interface CompanyShellWorkloadsManagerReturn {
  pendingRows: CompanyShellRow[];
  template: CompanyTemplateRef | null;
  mypayEnvironment: MyPayEnvironment | null;
  /** The Tier 1 workload id once resolved — `null` while create-or-reuse is still in flight. */
  workloadId: string | null;
  /** Null until a validate run reaches a terminal (`succeeded`/`failed`) status. */
  validateResult: CompanyShellValidateResult | null;
  /** True once a row has been edited since the last successful validate — mirrors `CompanyProvisionWizardState.validateStale`. */
  validateStale: boolean;
  managerError: string | null;
  /** True for the whole import-\>optional-PATCH-\>validate pipeline, not just the validate call. */
  isValidating: boolean;
  isCommitting: boolean;
  runId: string | null;
  runStatus: MigrationRunStatus | null;
  hasData: boolean;
  /**
   * Push a fresh batch in (new upload) or refresh the template/environment
   * for the currently-held batch. Resets all in-flight import/validate/run
   * state — callers must re-run `validate()` afterwards.
   */
  setPendingData: (
    rows: CompanyShellRow[],
    template: CompanyTemplateRef | null,
    mypayEnvironment: MyPayEnvironment | null,
    file?: File
  ) => void;
  /** Apply a single edited row (Review screen's per-cell editing) and mark the result stale. */
  updateRow: (index: number, next: CompanyShellRow) => void;
  validate: () => void;
  commit: () => void;
  clear: () => void;
}

/**
 * Orchestrates the bulk company-shell creation flow via the Slice 5
 * workloads REST surface. See the module-level doc comment above for the
 * pipeline shape, the two-hook-to-one-hook divergence from Track 1, and the
 * ETag/If-Match residual.
 */
export function useCompanyShellWorkloadsManager(): CompanyShellWorkloadsManagerReturn {
  const [pendingRows, setPendingRows] = useState<CompanyShellRow[]>([]);
  const [template, setTemplate] = useState<CompanyTemplateRef | null>(null);
  const [mypayEnvironment, setMypayEnvironmentState] =
    useState<MyPayEnvironment | null>(null);
  /** Imperative pre-flight guard errors only; stage failures are derived below. */
  const [guardError, setGuardError] = useState<string | null>(null);
  const [validationStale, setValidationStale] = useState(false);
  /**
   * True from a `validate()` call until the import-\>optional-PATCH-\>validate
   * pipeline reaches a terminal state (success or a stage failure). Drives
   * the reactive effect below; one flag covers the whole multi-stage
   * pipeline since only one stage is ever in flight at a time.
   */
  const [isPipelineActive, setIsPipelineActive] = useState(false);
  /**
   * When source-import ends `failed` because Pacific pre-flight blocked the
   * batch (e.g. phone format #1015), the Import job only carries an opaque
   * `outcome_summary` count string — the detailed `ValidationReport` is on
   * the entity record (Api still upserts on first import). Fetch it so Review
   * can anchor errors to cells instead of showing "1 error(s), 0 warning(s)".
   */
  const [importBlockedReport, setImportBlockedReport] =
    useState<ValidationReport | null>(null);

  /** Synchronous ref mirrors — prevent React state-batching races (same rationale as `useWorkPatternImportWorkloadsManager`). */
  const pendingRowsRef = useRef<CompanyShellRow[]>([]);
  const templateRef = useRef<CompanyTemplateRef | null>(null);
  const mypayEnvironmentRef = useRef<MyPayEnvironment | null>(null);
  const pendingFileRef = useRef<File | null>(null);
  /**
   * Gates the async if_match lookup + import kickoff so the driver effect
   * cannot fire concurrent `getOnboardingEntityRecord` / `createOnboardingImport`
   * calls while awaiting the re-import precondition (LADR-039 R-S1-1).
   */
  const importKickoffInFlightRef = useRef(false);
  /**
   * Once the import stage reaches a terminal status (succeeded **or** soft-
   * failed with Pacific findings), skip re-entering stage 1 so PATCH →
   * validate can run after inline edits without being blocked by a failed
   * Import job status.
   */
  const importStageSettledRef = useRef(false);

  // ------------------------------------------------------------------
  // Composed workloads hooks (Phase 1, Steps 4/5/5b/5c)
  // ------------------------------------------------------------------

  const workload = useOnboardingWorkload({
    // Bulk company-shell creation has no single company code (see module
    // doc) — override the default `enabled: Boolean(companyCode)` gate.
    companyCode: undefined,
    destinationSystem: DESTINATION_SYSTEM,
    enabled: true,
  });
  const importState = useOnboardingImport();
  const validationState = useOnboardingValidation();
  const runState = useOnboardingMigrationRun();

  /**
   * No dedicated `useOnboardingEntityRecordPatch` hook exists yet (see
   * `src/onboarding/hooks/index.ts`), so this is a local, one-off mutation
   * wrapping the generated `patchOnboardingEntityRecord` client directly —
   * matching the "Prefer generated API clients" rule in the root `AGENTS.md`
   * and the precedent already set by `useWorkPatternImportWorkloadsManager`.
   */
  const patchMutation = useMutation({
    mutationFn: async (args: {
      workloadId: string;
      rows: CompanyShellRow[];
    }) => {
      const entityRecordPatch: EntityRecordPatch = {
        canonical_entities: args.rows.map(toPatchCompanyShellRow),
      };
      const current = (await api.getOnboardingEntityRecord(
        args.workloadId,
        COMPANY_SHELL_ENTITY_TYPE
      )) as Awaited<ReturnType<typeof api.getOnboardingEntityRecord>> & {
        etag?: string;
      };
      return api.patchOnboardingEntityRecord(
        args.workloadId,
        COMPANY_SHELL_ENTITY_TYPE,
        entityRecordPatch,
        current.etag
          ? { headers: { "If-Match": current.etag } }
          : undefined
      );
    },
  });

  // Soft-failed import: load the entity's ValidationReport for the Review grid.
  useEffect(() => {
    if (importState.status !== JobStatus.failed) {
      setImportBlockedReport(null);
      return;
    }
    const workloadId = workload.data?.workloadId;
    if (!workloadId) return;

    let cancelled = false;
    void (async () => {
      try {
        const record = await api.getOnboardingEntityRecord(
          workloadId,
          COMPANY_SHELL_ENTITY_TYPE
        );
        if (cancelled) return;
        setImportBlockedReport(
          reportHasFindings(record.validation) ? record.validation! : null
        );
      } catch {
        if (!cancelled) setImportBlockedReport(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    importState.status,
    importState.importId,
    workload.data?.workloadId,
  ]);

  // ------------------------------------------------------------------
  // Driver effect — advances the import -> optional PATCH -> validate
  // pipeline one stage at a time. Each branch either kicks off the next
  // not-yet-started stage or returns early to wait for the in-flight one to
  // settle; the effect re-runs whenever any stage's status changes. Mirrors
  // `useWorkPatternImportWorkloadsManager`'s driver effect.
  //
  // `importState.invoke`/`validationState.invoke`/`patchMutation.mutate` are
  // intentionally omitted from the dependency array: `invoke` is a
  // `useCallback([])`-wrapped stable reference and `useMutation`'s `mutate`
  // is stable across renders regardless of the inline `mutationFn` closure
  // identity.
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isPipelineActive) return;

    if (workload.isError) {
      setIsPipelineActive(false);
      return;
    }
    const workloadId = workload.data?.workloadId;
    if (!workloadId) {
      // Workload query still resolving (create-or-reuse); wait for it.
      return;
    }

    // Stage 1: import the source file once per batch. Soft-failed imports
    // (Pacific pre-flight on the entity) still settle this stage so later
    // PATCH → validate after edits is not blocked by `status: failed`.
    if (!importStageSettledRef.current) {
      if (importState.error) {
        importKickoffInFlightRef.current = false;
        setIsPipelineActive(false);
        return;
      }
      if (importState.status === JobStatus.succeeded) {
        importKickoffInFlightRef.current = false;
        importStageSettledRef.current = true;
        // Fall through to stage 2/3.
      } else if (importState.status === JobStatus.failed) {
        importKickoffInFlightRef.current = false;
        importStageSettledRef.current = true;
        // Stop here so Review can render entity ValidationReport findings;
        // a later validate() after edits re-enters at stage 2.
        setIsPipelineActive(false);
        return;
      } else if (!importState.isInvoking && !importState.importId) {
        const sourceFile = pendingFileRef.current;
        if (!sourceFile) {
          // Defensive only -- `validate()` already refuses to activate the
          // pipeline in this exact state (no prior import and no file), so
          // this branch is unreachable through the public API today. Left in
          // (and intentionally uncovered) so a future change to `validate()`'s
          // guard can't silently let the pipeline spin forever waiting on a
          // file that will never arrive.
          setIsPipelineActive(false);
          setGuardError("No file to import. Please upload a CSV file first.");
          return;
        }
        if (importKickoffInFlightRef.current) {
          return;
        }
        importKickoffInFlightRef.current = true;
        // API `CompanyShellSourceImportAdapter` + router allowlist expect a
        // JSON array of CompanyShellRow (portal already parsed the CSV).
        // LADR-039 R-S1-1: re-import against an existing entity record must
        // echo `source_metadata.source_version` as `if_match_source_version`.
        const file = rowsToJsonImportFile(
          pendingRowsRef.current,
          sourceFile.name
        );
        const configCode = templateRef.current?.config_code;
        const configVersion = templateRef.current?.version;
        void (async () => {
          try {
            const ifMatchSourceVersion = await resolveIfMatchSourceVersion(
              workloadId,
              COMPANY_SHELL_ENTITY_TYPE
            );
            importState.invoke({
              workloadId,
              file,
              entityType: COMPANY_SHELL_ENTITY_TYPE,
              configCode,
              configVersion,
              ifMatchSourceVersion,
            });
          } catch {
            importKickoffInFlightRef.current = false;
            setIsPipelineActive(false);
            setGuardError(
              "Could not check existing import state before uploading. Please try again."
            );
          }
        })();
        return;
      } else {
        // Import still queued/running — wait.
        return;
      }
    }

    // Stage 2: PATCH the held canonical rows if the client made edits since
    // the last successful import/patch (W2 — never re-CSV for edits).
    if (validationStale) {
      if (patchMutation.isError) {
        setIsPipelineActive(false);
        return;
      }
      if (patchMutation.isSuccess) {
        // The PATCH landed; clear the staleness flag so the *next* effect
        // run (triggered by this state change) falls through to stage 3
        // instead of re-entering this branch and looping forever — same fix
        // already applied in `useWorkPatternImportWorkloadsManager`.
        setValidationStale(false);
        return;
      }
      if (!patchMutation.isPending) {
        patchMutation.mutate({
          workloadId,
          rows: pendingRowsRef.current,
        });
      }
      return;
    }

    // Stage 3: validate. `destinationDryRun: true` requests the Christmas
    // dry-run in addition to Pacific validation, for parity with Track 1
    // (whose `company_shell_provision` workflow always validated against
    // Christmas). Gated server-side by `ONBOARDING_MIGRATION_RUNS_ENABLED` —
    // Api #1051 shipped this dark (false in Sandbox/Preview/Production until
    // a deliberate cutover) — not re-checked client-side.
    if (validationState.status !== JobStatus.succeeded) {
      if (validationState.status === JobStatus.failed || validationState.error) {
        setIsPipelineActive(false);
        return;
      }
      if (!validationState.isInvoking && !validationState.validationId) {
        const batchMypayEnvironment = mypayEnvironmentRef.current;
        if (!batchMypayEnvironment) {
          // Defensive only -- same reasoning as the "no file" branch above:
          // `validate()` already refuses to activate the pipeline without a
          // `mypayEnvironment`, so this is unreachable via the public API.
          setIsPipelineActive(false);
          setGuardError(
            "No MyPay environment selected. Please choose an environment before validating."
          );
          return;
        }
        validationState.invoke({
          workloadId,
          entityType: COMPANY_SHELL_ENTITY_TYPE,
          destinationDryRun: true,
          mypayEnvironment: batchMypayEnvironment,
          targetEnvironment: TargetEnvironment.test,
        });
      }
      return;
    }

    // All stages terminal-succeeded.
    setIsPipelineActive(false);
    setValidationStale(false);
    setImportBlockedReport(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPipelineActive,
    workload.isError,
    workload.data?.workloadId,
    importState.status,
    importState.isInvoking,
    importState.importId,
    validationStale,
    patchMutation.isPending,
    patchMutation.isSuccess,
    patchMutation.isError,
    validationState.status,
    validationState.isInvoking,
    validationState.validationId,
  ]);

  // ------------------------------------------------------------------
  // Derived result state
  // ------------------------------------------------------------------

  const validateResult = useMemo<CompanyShellValidateResult | null>(() => {
    // LADR-039 / Api: blocking Pacific or Christmas dry-run errors mark the
    // Validation job `failed` while still attaching a `report` (see
    // WorkloadsService.validate_entity: `FAILED if report.is_blocked`). Map
    // that report so Review can show row errors instead of a generic banner.
    if (validationState.status === JobStatus.succeeded) {
      return mapValidationReportToCompanyShellValidateResult(
        validationState.data?.report,
        pendingRows.length
      );
    }
    if (validationState.status === JobStatus.failed) {
      const report = validationState.data?.report;
      if (!reportHasFindings(report)) return null;
      return mapValidationReportToCompanyShellValidateResult(
        report,
        pendingRows.length
      );
    }
    // Import soft-fail: Pacific findings live on the entity record, not on
    // ImportView — surface them before a Validation job has run.
    if (importBlockedReport) {
      return mapValidationReportToCompanyShellValidateResult(
        importBlockedReport,
        pendingRows.length
      );
    }
    return null;
  }, [
    validationState.status,
    validationState.data,
    pendingRows.length,
    importBlockedReport,
  ]);

  // Simplified error surfacing vs Track 1's `AgentInvocationStatus.error`
  // classification: the workloads REST API has a different error shape, so
  // this derives one generic message per stage — mirrors
  // `useWorkPatternImportWorkloadsManager`'s `managerError`.
  const managerError = useMemo<string | null>(() => {
    if (guardError) return guardError;
    if (workload.isError) {
      return "Could not create or load the onboarding workload. Please try again.";
    }
    if (importState.error) {
      return `Import failed to start: ${importState.error}`;
    }
    if (importState.status === JobStatus.failed) {
      // Soft-failed Import jobs stay `failed` even after the user edits and
      // a later PATCH → Validation succeeds (or fails with its own report).
      // Prefer the grid / Validation job — never keep Import's
      // `outcome_summary` banner beside a green "All rows passed" result.
      if (
        validationState.status === JobStatus.succeeded ||
        validationState.status === JobStatus.failed ||
        importBlockedReport
      ) {
        return null;
      }
      return (
        importState.data?.outcome_summary ??
        "The file import failed. Please try again."
      );
    }
    if (patchMutation.isError) {
      return "Could not save your edits before re-validating. Please try again.";
    }
    if (validationState.error) {
      return `Validation failed to start: ${validationState.error}`;
    }
    if (validationState.status === JobStatus.failed) {
      // Prefer row-level report errors (Christmas dry-run 409, Pacific rules,
      // etc.) over a opaque banner — commit stays blocked via
      // `validationState.status !== succeeded` / canCommit on errors.
      const report = validationState.data?.report;
      if (!reportHasFindings(report)) {
        return "Validation failed. Please try again.";
      }
    }
    if (runState.error) {
      return `Import run failed to start: ${runState.error}`;
    }
    if (runState.status === MigrationRunStatus.failed) {
      return "The import run failed. Please try again.";
    }
    return null;
  }, [
    guardError,
    workload.isError,
    importState.error,
    importState.status,
    importState.data?.outcome_summary,
    importBlockedReport,
    patchMutation.isError,
    validationState.error,
    validationState.status,
    validationState.data?.report,
    runState.error,
    runState.status,
  ]);

  // ------------------------------------------------------------------
  // Public interface
  // ------------------------------------------------------------------

  const setPendingData = useCallback(
    (
      rows: CompanyShellRow[],
      nextTemplate: CompanyTemplateRef | null,
      nextMypayEnvironment: MyPayEnvironment | null,
      file?: File
    ) => {
      pendingRowsRef.current = rows;
      templateRef.current = nextTemplate;
      mypayEnvironmentRef.current = nextMypayEnvironment;
      pendingFileRef.current = file ?? null;
      importKickoffInFlightRef.current = false;
      importStageSettledRef.current = false;
      setPendingRows(rows);
      setTemplate(nextTemplate);
      setMypayEnvironmentState(nextMypayEnvironment);
      setValidationStale(false);
      setGuardError(null);
      setImportBlockedReport(null);
      setIsPipelineActive(false);
      importState.reset();
      validationState.reset();
      runState.reset();
      patchMutation.reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateRow = useCallback(
    (index: number, next: CompanyShellRow) => {
      const updated = pendingRowsRef.current.map((row, i) =>
        i === index ? next : row
      );
      pendingRowsRef.current = updated;
      setPendingRows(updated);
      // Keep the existing validate result on screen (Review's own gating
      // reads `validateStale` to decide whether to still trust it) but mark
      // it stale so the next `validate()` re-enters the PATCH stage —
      // mirrors `BulkCreateCompanyReviewScreen.handleCellChange`'s Track 1
      // behaviour of setting `validateStale: true` on every edit.
      setValidationStale(true);
      validationState.reset();
      runState.reset();
      patchMutation.reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const validate = useCallback((): void => {
    const rows = pendingRowsRef.current;
    if (rows.length === 0) return;

    if (!templateRef.current) {
      setGuardError(
        "No configuration template selected. Please choose a template before validating."
      );
      return;
    }
    if (!mypayEnvironmentRef.current) {
      setGuardError(
        "No MyPay environment selected. Please choose an environment before validating."
      );
      return;
    }
    // A file is only required the first time this batch is imported; a
    // re-validate after an edit reuses the already-imported server record
    // (via PATCH) and needs no file.
    if (importState.importId === null && !pendingFileRef.current) {
      setGuardError("No file to import. Please upload a CSV file first.");
      return;
    }

    setGuardError(null);
    setIsPipelineActive(true);
  }, [importState.importId]);

  const commit = useCallback((): void => {
    const rows = pendingRowsRef.current;
    const workloadId = workload.data?.workloadId;
    const batchMypayEnvironment = mypayEnvironmentRef.current;

    if (
      rows.length === 0 ||
      !workloadId ||
      !batchMypayEnvironment ||
      validationStale ||
      validationState.status !== JobStatus.succeeded ||
      // Defence-in-depth: `BulkCreateCompanyReviewScreen.canCommit` already
      // blocks the button on `errors.length > 0`, but company shell has no
      // separate warning-acknowledgment override (unlike work
      // patterns/pay components — see `companyShellWorkloadsResultMapper.ts`'s
      // errors/warnings-merge residual), so a stray direct `commit()` call
      // must not be able to push malformed companies to Christmas.
      (validateResult?.errors.some((error) => error.severity !== "warning") ??
        false)
    ) {
      return;
    }

    setGuardError(null);
    runState.invoke({
      workloadId,
      targetEnvironment: TargetEnvironment.test,
      entityTypes: [COMPANY_SHELL_ENTITY_TYPE],
      mypayEnvironment: batchMypayEnvironment,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationStale, validationState.status, validateResult, workload.data?.workloadId]);

  const clear = useCallback(() => {
    pendingRowsRef.current = [];
    templateRef.current = null;
    mypayEnvironmentRef.current = null;
    pendingFileRef.current = null;
    importKickoffInFlightRef.current = false;
    importStageSettledRef.current = false;
    setPendingRows([]);
    setTemplate(null);
    setMypayEnvironmentState(null);
    setGuardError(null);
    setValidationStale(false);
    setImportBlockedReport(null);
    setIsPipelineActive(false);
    importState.reset();
    validationState.reset();
    runState.reset();
    patchMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  // The whole import->PATCH->validate pipeline is a single flag, so
  // "validating" is just "pipeline in flight" — mirrors
  // `useWorkPatternImportWorkloadsManager.isValidating`.
  const isValidating = isPipelineActive;
  const isCommitting = runState.isInvoking || runState.isPolling;

  return {
    pendingRows,
    template,
    mypayEnvironment,
    workloadId: workload.data?.workloadId ?? null,
    validateResult,
    validateStale: validationStale,
    managerError,
    isValidating,
    isCommitting,
    runId: runState.runId,
    runStatus: runState.status,
    hasData: pendingRows.length > 0,
    setPendingData,
    updateRow,
    validate,
    commit,
    clear,
  };
}
